/**
 * The one screen you cannot walk away from.
 *
 * No close button and no Escape. The only way out is forward, because the
 * entire point of a chapter gate is that it is the single thing in the game the
 * player does not get to decline. Everything else — an event, a favour, a
 * shortfall — is a choice with a price.
 *
 * It takes a shape rather than a chapter: a title, a speaker, a kicker and some
 * lines. Which is why a suspension notice comes through here too — it is the
 * same thing wearing the same clothes, a fortnight of the calendar handed to
 * you with no button that gives it back, and it would be absurd to build a
 * second undismissable modal to say so.
 */
export function createChapterPanel({ parent, onDone }) {
  const root = document.createElement('div');
  root.className = 'chapter';
  root.hidden = true;
  parent.append(root);

  let chapter = null;
  let index = 0;
  /**
   * Who to tell when this one is finished with.
   *
   * A chapter reports to the gate that armed it; a notice reports to whoever
   * put it up. Held per showing rather than per panel so the two cannot end up
   * marking each other's homework.
   */
  let then = null;

  function render() {
    const last = index >= chapter.lines.length - 1;
    root.innerHTML = `
      <div class="chapter-card" data-kind="${chapter.kind ?? 'chapter'}">
        <span class="chapter-kicker">${chapter.kicker ?? 'A word'}</span>
        <h2>${chapter.title}</h2>
        <span class="chapter-speaker">${chapter.speaker}</span>
        <p class="chapter-line">${chapter.lines[index]}</p>
        <div class="chapter-dots">${chapter.lines
          .map((_, i) => `<i data-on="${i <= index ? 'yes' : 'no'}"></i>`)
          .join('')}</div>
        <button class="chapter-next" type="button">${
          last ? (chapter.done ?? 'Get back to work') : 'Go on'
        }</button>
      </div>`;
    root.querySelector('.chapter-next').addEventListener('click', advance);
  }

  function advance() {
    if (index < chapter.lines.length - 1) {
      index += 1;
      render();
      return;
    }
    root.hidden = true;
    const finished = chapter;
    const reply = then;
    chapter = null;
    then = null;
    (reply ?? onDone)?.(finished);
  }

  return {
    root,
    get open() {
      return !root.hidden;
    },
    /** Advances a line. Bound to the interact key as well as the button. */
    advance() {
      if (!root.hidden) advance();
    },
    show(next, { onDone: after = null } = {}) {
      chapter = next;
      then = after;
      index = 0;
      render();
      root.hidden = false;
    },
  };
}
